import React, { useEffect, useState } from "react";
import type { ActiveLayer } from "@nexus/contracts";
import type { TimelineEntry } from "../types";
import { LAYER_METADATA } from "../types";
import {
  Play,
  Pause,
  RotateCcw,
  SkipBack,
  SkipForward,
  Calendar,
  Clock,
} from "lucide-react";

interface TimelineScrubberProps {
  entries: TimelineEntry[];
  currentDate: string; // "2023-01-01" to "2026-06-30" or ISO
  onChangeDate: (dateIso: string) => void;
  activeLayers: Set<ActiveLayer>;
  onSelectEventId: (eventId: string) => void;
}

const START_TIME = new Date("2023-01-01T00:00:00.000Z").getTime();
const END_TIME = new Date("2026-06-30T23:59:59.000Z").getTime();
const TIME_RANGE = END_TIME - START_TIME;

export const TimelineScrubber: React.FC<TimelineScrubberProps> = ({
  entries,
  currentDate,
  onChangeDate,
  activeLayers,
  onSelectEventId,
}) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const currentMs = new Date(currentDate).getTime();
  const currentProgressPct = Math.max(
    0,
    Math.min(100, ((currentMs - START_TIME) / TIME_RANGE) * 100),
  );

  // Playback timer
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      const nextMs = currentMs + 1000 * 60 * 60 * 24 * 15; // Step 15 days
      if (nextMs >= END_TIME) {
        onChangeDate(new Date(END_TIME).toISOString());
        setIsPlaying(false);
      } else {
        onChangeDate(new Date(nextMs).toISOString());
      }
    }, 400);

    return () => clearInterval(interval);
  }, [isPlaying, currentMs, onChangeDate]);

  const handleSliderChange = (pct: number) => {
    const targetMs = START_TIME + (pct / 100) * TIME_RANGE;
    onChangeDate(new Date(targetMs).toISOString());
  };

  const stepBack = () => {
    const prevMs = Math.max(START_TIME, currentMs - 1000 * 60 * 60 * 24 * 30);
    onChangeDate(new Date(prevMs).toISOString());
  };

  const stepForward = () => {
    const nextMs = Math.min(END_TIME, currentMs + 1000 * 60 * 60 * 24 * 30);
    onChangeDate(new Date(nextMs).toISOString());
  };

  const resetTimeline = () => {
    onChangeDate(new Date(END_TIME).toISOString());
    setIsPlaying(false);
  };

  const formattedCurrentDate = new Date(currentDate).toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

  return (
    <footer className="cc-timeline">
      <div className="timeline-controls-row">
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div className="timeline-period-label">
            <Clock size={15} />
            <span>asOf 시점: {formattedCurrentDate}</span>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 400 }}>
              (범위: 2023.01 ~ 2026-H1)
            </span>
          </div>
        </div>

        {/* Scrubber Playback Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <button className="btn-icon" onClick={stepBack} title="1개월 이전">
            <SkipBack size={13} />
          </button>
          <button
            className="btn-icon"
            onClick={() => setIsPlaying(!isPlaying)}
            title={isPlaying ? "일시정지" : "시계열 재생"}
            style={{ color: isPlaying ? "var(--accent-cyan)" : undefined }}
          >
            {isPlaying ? <Pause size={13} /> : <Play size={13} />}
          </button>
          <button className="btn-icon" onClick={stepForward} title="1개월 이후">
            <SkipForward size={13} />
          </button>
          <button className="btn-icon" onClick={resetTimeline} title="2026-H1 최신 시점으로 이동">
            <RotateCcw size={13} />
          </button>
        </div>

        <div style={{ fontSize: "11px", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "6px" }}>
          <Calendar size={13} />
          <span>등록된 타임라인 사건: <strong style={{ color: "#ffffff" }}>{entries.length}개</strong></span>
        </div>
      </div>

      {/* Scrubber Bar Track */}
      <div className="timeline-scrubber-track">
        {/* Event Ticks */}
        <div className="timeline-event-ticks">
          {entries.map((entry) => {
            const entryMs = new Date(entry.occurredAt).getTime();
            const leftPct = ((entryMs - START_TIME) / TIME_RANGE) * 100;
            if (leftPct < 0 || leftPct > 100) return null;

            const primaryLayer = entry.layers.find((l: ActiveLayer) =>
              activeLayers.has(l),
            ) as ActiveLayer | undefined;
            const tickColor = primaryLayer
              ? LAYER_METADATA[primaryLayer]?.color
              : "rgba(255,255,255,0.3)";

            return (
              <div
                key={entry.eventId}
                className="timeline-event-tick"
                style={
                  {
                    left: `${leftPct}%`,
                    "--tick-color": tickColor,
                  } as React.CSSProperties
                }
                title={`${new Date(entry.occurredAt).toLocaleDateString()}: ${entry.titleKo} (${entry.layers.join(", ")})`}
                onClick={() => onSelectEventId(entry.eventId)}
              />
            );
          })}
        </div>

        {/* Range Input Slider */}
        <input
          type="range"
          min="0"
          max="100"
          step="0.1"
          value={currentProgressPct}
          onChange={(e) => handleSliderChange(parseFloat(e.target.value))}
          style={{ position: "relative", zIndex: 10 }}
        />
      </div>

      {/* Year labels */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "10px",
          color: "var(--text-muted)",
          fontFamily: "var(--font-mono)",
          marginTop: "2px",
        }}
      >
        <span>2023.01</span>
        <span>2024.01</span>
        <span>2025.01</span>
        <span>2026.01</span>
        <span style={{ color: "var(--accent-cyan)", fontWeight: 700 }}>2026-H1 (CURRENT)</span>
      </div>
    </footer>
  );
};
